/**
 * Brokered GitHub blocker extraction and copy (moved out of session-logic.ts
 * for the timeline port, ticket 3525; behavior unchanged). The contract types
 * are re-exported from session-logic so the forge vocabulary keeps one import
 * surface.
 *
 * @module workLogGitHub
 */

export interface GitHubToolConfirmationRequest {
  confirmationId: string;
  toolName: string;
  operation: string;
  request: Record<string, unknown>;
}

export interface GitHubActionRequiredRequest {
  action: string;
  toolName: string | null;
  message: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function asTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function extractGitHubConfirmationRequest(
  payload: Record<string, unknown> | null,
): GitHubToolConfirmationRequest | null {
  for (const parsed of extractGitHubToolResultPayloads(payload)) {
    if (parsed.userActionRequired !== "confirm_github_tool") continue;
    const confirmation = asRecord(parsed.confirmation);
    const request = asRecord(confirmation?.request);
    const confirmationId = asTrimmedString(confirmation?.confirmationId);
    const toolName = asTrimmedString(confirmation?.toolName);
    const operation = asTrimmedString(confirmation?.operation);
    if (!confirmationId || !toolName || !operation || !request) {
      continue;
    }
    return { confirmationId, toolName, operation, request };
  }
  return null;
}

export function extractGitHubActionRequiredRequest(
  payload: Record<string, unknown> | null,
): GitHubActionRequiredRequest | null {
  const parsed = extractGitHubToolResultPayloads(payload)[0];
  if (!parsed) return null;
  const action = asTrimmedString(parsed.userActionRequired);
  if (!action || action === "confirm_github_tool") return null;
  return {
    action,
    toolName: asTrimmedString(parsed.toolName),
    message: asTrimmedString(parsed.error) ?? asTrimmedString(parsed.message),
  };
}

function extractGitHubToolResultPayloads(
  payload: Record<string, unknown> | null,
): Record<string, unknown>[] {
  if (!payload) return [];
  const results: Record<string, unknown>[] = [];
  const seen = new Set<unknown>();

  // fallow-ignore-next-line complexity -- port of forge session-logic (pre-port) at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
  const visit = (value: unknown, depth: number): void => {
    if (depth > 8 || value === null || value === undefined) return;

    if (typeof value === "string") {
      const text = asTrimmedString(value);
      if (!text) return;
      const parsed = parseJsonObject(text);
      if (parsed && asTrimmedString(parsed.userActionRequired)) {
        results.push(parsed);
      }
      return;
    }

    if (typeof value !== "object" || seen.has(value)) return;
    seen.add(value);

    if (Array.isArray(value)) {
      for (const item of value) {
        visit(item, depth + 1);
      }
      return;
    }

    const record = value as Record<string, unknown>;
    const directAction = asTrimmedString(record.userActionRequired);
    if (directAction) {
      results.push(record);
      return;
    }

    for (const child of Object.values(record)) {
      visit(child, depth + 1);
    }
  };

  visit(payload, 0);
  return results;
}

function parseJsonObject(value: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value);
    return asRecord(parsed);
  } catch {
    return null;
  }
}

export function formatGitHubOperationLabel(operation: string): string {
  switch (operation) {
    case "create_repository":
      return "Create GitHub repository";
    case "link_project_repository":
      return "Link project repository";
    case "push_branch":
      return "Push Git branch";
    case "create_pull_request":
      return "Create pull request";
    case "rerun_workflow":
      return "Rerun GitHub Actions workflow";
    default:
      return operation
        .split("_")
        .filter(Boolean)
        .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
        .join(" ");
  }
}

export function formatGitHubConfirmationDetail(
  confirmation: GitHubToolConfirmationRequest,
): string {
  const repoFullName =
    asTrimmedString(confirmation.request.repoFullName) ??
    (asTrimmedString(confirmation.request.owner) && asTrimmedString(confirmation.request.name)
      ? `${asTrimmedString(confirmation.request.owner)}/${asTrimmedString(confirmation.request.name)}`
      : null);
  const cwd = asTrimmedString(confirmation.request.cwd);
  const branch = asTrimmedString(confirmation.request.branch);
  const title = asTrimmedString(confirmation.request.title);
  return [repoFullName, cwd, branch, title].filter(Boolean).join(" - ");
}

export function formatGitHubActionRequiredLabel(action: string): string {
  switch (action) {
    case "connect_github":
      return "Connect GitHub to continue";
    case "connect_cmmd":
      return "Forge couldn't authorize this action";
    case "start_from_authenticated_forge_session":
      return "Open this thread in Forge";
    case "link_project_repository":
      return "Link this project to GitHub";
    case "create_branch":
      return "Create a branch before pushing";
    case "retry_in_user_vm":
      return "Retry in the project VM";
    default:
      return action
        .split("_")
        .filter(Boolean)
        .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
        .join(" ");
  }
}

export function formatGitHubActionRequiredDetail(action: string): string {
  switch (action) {
    case "connect_github":
      return "Connect GitHub so agents can create repos, push branches, and open PRs.";
    case "connect_cmmd":
      return "Reconnect Forge, then retry. Your conversation is preserved.";
    case "start_from_authenticated_forge_session":
      return "Open it in Forge so this action uses your signed-in account.";
    case "link_project_repository":
      return "Link a GitHub repo to this project before pushing or opening PRs.";
    case "create_branch":
      return "Forge needs a branch before it can push to GitHub.";
    case "retry_in_user_vm":
      return "This has to run from the project's cloud VM.";
    default:
      return "Finish the required setup, then retry.";
  }
}

/**
 * Resolves the detail line shown for a GitHub action-required blocker.
 *
 * The server-provided `message` is preferred for most actions because it can
 * carry request-specific context. `connect_cmmd` is the exception: its server
 * message may assert that a session expired even when the runtime is simply on
 * an old build. The action code cannot distinguish those causes, so we override
 * it with neutral recovery copy rather than presenting an unsupported diagnosis. Single source of
 * truth for both the transcript row and the composer panel.
 */
export function resolveGitHubActionRequiredDetail(
  actionRequired: GitHubActionRequiredRequest,
): string {
  if (actionRequired.action === "connect_cmmd") {
    return formatGitHubActionRequiredDetail(actionRequired.action);
  }
  return actionRequired.message ?? formatGitHubActionRequiredDetail(actionRequired.action);
}

/** Structural equality for two action-required requests (identity of a blocker). */
export function sameGitHubActionRequired(
  a: GitHubActionRequiredRequest,
  b: GitHubActionRequiredRequest,
): boolean {
  return a.action === b.action && a.toolName === b.toolName && a.message === b.message;
}

/** Label for the primary settings/sign-in CTA button on an action-required blocker. */
export function actionRequiredCtaLabel(action: string): string {
  switch (action) {
    case "connect_cmmd":
      return "Reconnect Forge";
    case "connect_github":
      return "Connect GitHub";
    default:
      return "Open Git settings";
  }
}

/**
 * Retry instruction Forge hands the agent once the user resolves the blocker.
 * Kept alongside the copy so the transcript row and composer panel emit the
 * identical retry text.
 */
export function buildGitHubActionRequiredRetryText(
  actionRequired: GitHubActionRequiredRequest,
): string {
  const toolClause = actionRequired.toolName
    ? `Retry the previous GitHub tool request for \`${actionRequired.toolName}\`.`
    : "Retry the previous GitHub tool request.";
  return [
    "The required Forge GitHub setup has been completed by the user.",
    toolClause,
    "Continue the previous GitHub lifecycle request using Forge brokered GitHub tools only.",
    "Do not use shell `gh` or direct GitHub CLI commands.",
  ].join("\n");
}
